import {
	fetchBaseQuery,
	type BaseQueryFn,
	type FetchArgs,
	type FetchBaseQueryError,
} from '@reduxjs/toolkit/query'

import type { TokenResponse } from './types/user'

/**
 * Default API URL — falls back to localhost dev gateway.
 *
 * Resolution order:
 *   1. `import.meta.env.VITE_API_URL` (Vite consumers — `@rentflowdev/web`)
 *   2. `process.env.VITE_API_URL` (Node consumers — tests, SSR)
 *   3. `http://localhost:4000` (last-resort dev fallback)
 */
function resolveBaseUrl(): string {
	// `import.meta.env` is only available in module-form bundlers (Vite, etc.).
	// We probe it defensively so this package stays runtime-agnostic.
	try {
		const meta = (import.meta as unknown as { env?: Record<string, string | undefined> }).env
		if (meta?.VITE_API_URL) return meta.VITE_API_URL
	} catch {
		/* no-op — environment without import.meta.env support */
	}

	if (typeof process !== 'undefined' && process.env?.VITE_API_URL) {
		return process.env.VITE_API_URL
	}

	return 'http://localhost:4000'
}

/** Storage keys used to read/write tokens. Consumers can override via `configureAuthStorage`. */
const STORAGE_KEYS = {
	access: 'rentflow.access_token',
	refresh: 'rentflow.refresh_token',
}

interface TokenStorageAdapter {
	getAccessToken(): string | null
	getRefreshToken(): string | null
	setTokens(access: string, refresh: string): void
	clearTokens(): void
}

const defaultStorage: TokenStorageAdapter = {
	getAccessToken() {
		if (typeof localStorage === 'undefined') return null
		return localStorage.getItem(STORAGE_KEYS.access)
	},
	getRefreshToken() {
		if (typeof localStorage === 'undefined') return null
		return localStorage.getItem(STORAGE_KEYS.refresh)
	},
	setTokens(access, refresh) {
		if (typeof localStorage === 'undefined') return
		localStorage.setItem(STORAGE_KEYS.access, access)
		localStorage.setItem(STORAGE_KEYS.refresh, refresh)
	},
	clearTokens() {
		if (typeof localStorage === 'undefined') return
		localStorage.removeItem(STORAGE_KEYS.access)
		localStorage.removeItem(STORAGE_KEYS.refresh)
	},
}

let storage: TokenStorageAdapter = defaultStorage

/**
 * Allow consumers (e.g. `@rentflowdev/web`) to swap in their own auth-token
 * adapter so tokens flow through their existing `lib/auth` module.
 */
export function configureAuthStorage(adapter: Partial<TokenStorageAdapter>): void {
	storage = {
		...defaultStorage,
		...adapter,
	}
}

const rawBaseQuery = fetchBaseQuery({
	baseUrl: resolveBaseUrl(),
	prepareHeaders: (headers) => {
		const token = storage.getAccessToken()
		if (token) headers.set('Authorization', `Bearer ${token}`)
		return headers
	},
})

/** De-dupe parallel refresh attempts triggered by concurrent 401s. */
let refreshPromise: Promise<boolean> | null = null

async function performRefresh(
	api: Parameters<BaseQueryFn>[1],
	extraOptions: Parameters<BaseQueryFn>[2]
): Promise<boolean> {
	const refreshToken = storage.getRefreshToken()
	if (!refreshToken) return false

	const refreshResult = await rawBaseQuery(
		{
			url: '/auth/refresh',
			method: 'POST',
			body: { refresh_token: refreshToken },
		},
		api,
		extraOptions
	)

	if (refreshResult.data) {
		const data = refreshResult.data as TokenResponse
		storage.setTokens(data.access_token, data.refresh_token)
		return true
	}
	return false
}

export const baseQueryWithReAuth: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (args, api, extraOptions) => {
	let result = await rawBaseQuery(args, api, extraOptions)

	if (result.error?.status === 401) {
		if (!refreshPromise) {
			refreshPromise = performRefresh(api, extraOptions).finally(() => {
				refreshPromise = null
			})
		}
		const ok = await refreshPromise
		if (ok) {
			result = await rawBaseQuery(args, api, extraOptions)
		} else {
			storage.clearTokens()
		}
	}

	return result
}
